// Import React library for creating components
import React from 'react';
// Import icon components from Lucide React icon library
import { X, AlertTriangle, Calendar, Clock } from 'lucide-react';
// Import date formatting utility from date-fns library
import { format } from 'date-fns';
// Import TypeScript type definition for deadline data structure
import type { MatterDeadline } from '../../lib/db';

// Define the shape of props this notification toast component expects
// TypeScript interface ensures we pass the correct data types
interface DeadlineNotificationToastProps {
  // deadline: The deadline data containing title, due date, matter info, etc.
  deadline: MatterDeadline;
  // type: Determines the visual style and urgency of the notification
  // 'overdue' = red styling for past due items
  // 'today' = orange styling for items due today
  // 'upcoming' = blue styling for future items
  type: 'overdue' | 'today' | 'upcoming';
  // onClose: Function to call when user dismisses/closes the notification
  onClose: () => void;
  // onClick: Function to call when user clicks on the notification to view details
  onClick: () => void;
}

// DeadlineNotificationToast displays a popup notification for deadline reminders
// It shows different colors and icons based on urgency (overdue, today, upcoming)
export function DeadlineNotificationToast({ 
  deadline, 
  type, 
  onClose, 
  onClick 
}: DeadlineNotificationToastProps) {
  // Function to choose the appropriate icon based on notification type
  // Returns different colored icons to indicate urgency level
  const getIcon = () => {
    switch (type) {
      case 'overdue':
        return <AlertTriangle className="w-5 h-5 text-red-500" />; // Red triangle for urgent/overdue
      case 'today':
        return <Clock className="w-5 h-5 text-orange-500" />;        // Orange clock for today
      case 'upcoming':
        return <Calendar className="w-5 h-5 text-blue-500" />;       // Blue calendar for future
    }
  };

  // Function to get the appropriate title text based on notification type
  // Returns user-friendly labels that clearly indicate the urgency
  const getTitle = () => {
    switch (type) {
      case 'overdue':
        return 'Overdue Deadline';   // Alert user that deadline has passed
      case 'today':
        return 'Due Today';          // Inform user deadline is today
      case 'upcoming':
        return 'Upcoming Deadline';  // Remind user of future deadline
    }
  };

  // Function to get appropriate background colors and borders based on notification type
  // Uses subtle colored backgrounds with matching borders for visual hierarchy
  const getBgColor = () => {
    switch (type) {
      case 'overdue':
        return 'bg-red-50 border-red-200';     // Light red background for urgent items
      case 'today':
        return 'bg-orange-50 border-orange-200'; // Light orange background for today's items
      case 'upcoming':
        return 'bg-blue-50 border-blue-200';   // Light blue background for upcoming items
    }
  };

  // Render the notification toast component
  return (
    // Main container with fixed width but responsive max-width, shadow, and dynamic background
    <div className={`w-[400px] max-w-[calc(100vw-2rem)] bg-white shadow-lg rounded-lg border ${getBgColor()}`}>
      <div className="p-4">
        {/* Main content layout: icon on left, content in middle, close button on right */}
        <div className="flex items-start gap-3">
          {/* Icon container - doesn't shrink and has slight top padding for alignment */}
          <div className="flex-shrink-0 pt-0.5">
            {getIcon()} {/* Display the appropriate icon based on notification type */}
          </div>
          {/* Main content area - takes remaining space and can shrink if needed */}
          <div className="flex-1 min-w-0">
            {/* Notification type title (Overdue Deadline, Due Today, etc.) */}
            <p className="text-sm font-medium text-gray-900">{getTitle()}</p>
            {/* Deadline title/description in bold */}
            <p className="mt-1 text-sm text-gray-900 font-semibold">{deadline.title}</p>
            {/* Matter name and optional client name */}
            <p className="mt-1 text-xs text-gray-600">
              {deadline.matter_name}
              {/* Show client name if available, separated by bullet point */}
              {deadline.client_name && ` • ${deadline.client_name}`}
            </p>
            {/* Formatted due date */}
            <p className="mt-1 text-xs text-gray-500">
              Due: {format(new Date(deadline.due_date), 'MMM d, yyyy')}
            </p>
            {/* Action buttons for the notification */}
            <div className="mt-3 flex items-center gap-3">
              {/* Primary action button - navigates to the matter */}
              <button
                onClick={onClick} // Call the onClick function passed from parent
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 rounded px-2 py-1"
              >
                View Matter
              </button>
              {/* Secondary action button - dismisses the notification */}
              <button
                onClick={onClose} // Call the onClose function passed from parent
                className="text-sm font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 rounded px-2 py-1"
              >
                Dismiss
              </button>
            </div>
          </div>
          {/* Close button in top-right corner */}
          <div className="flex-shrink-0">
            <button
              className="p-1 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500"
              onClick={onClose} // Close notification when clicked
            >
              {/* Screen reader text for accessibility */}
              <span className="sr-only">Close</span>
              {/* X icon for closing */}
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}